/**
 * Combobox.jsx — Robust combobox with searchable dropdown + free-text input.
 * Replaces <datalist> which has cross-browser quirks (cannot click to select with placeholder shown).
 */
import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export default function Combobox({
  value,
  onChange,
  options = [],      // [{ value, label, sublabel? }] OR [string]
  placeholder = '',
  testid,
  className = '',
  emptyMessage = 'Aucune correspondance',
  maxResults = 20,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapperRef = useRef(null);

  useEffect(() => setQuery(value || ''), [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const normOptions = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const filtered = query
    ? normOptions.filter((o) => o.label?.toLowerCase().includes(query.toLowerCase()))
    : normOptions;
  const limited = filtered.slice(0, maxResults);

  const handleSelect = (opt) => {
    onChange(opt.value, opt);
    setQuery(opt.label);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testid}
          className={`pr-8 ${className}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div
          data-testid={testid ? `${testid}-dropdown` : undefined}
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border-2 border-stone-300 shadow-lg"
        >
          {limited.length === 0 ? (
            <div className="px-3 py-2 text-xs text-stone-400 italic">{emptyMessage}</div>
          ) : (
            limited.map((opt) => (
              <button
                key={opt.value}
                type="button"
                data-testid={testid ? `${testid}-option-${opt.value}` : undefined}
                onClick={() => handleSelect(opt)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-stone-100 border-b border-stone-100 last:border-b-0 flex items-start justify-between gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#09090B] truncate">{opt.label}</div>
                  {opt.sublabel && (
                    <div className="text-[11px] text-stone-500 truncate">{opt.sublabel}</div>
                  )}
                </div>
                {opt.value === value && <Check className="w-4 h-4 text-[#DA291C] flex-shrink-0 mt-0.5" />}
              </button>
            ))
          )}
          {filtered.length > maxResults && (
            <div className="px-3 py-1.5 text-[10px] text-stone-400 italic bg-stone-50 border-t border-stone-200">
              {filtered.length - maxResults} autres résultats — affinez votre recherche
            </div>
          )}
        </div>
      )}
    </div>
  );
}
